import type { AssetCategoryView } from '@/api/asset'

/** 分组层级上限，顶级为第 1 级（与 Go 的 db.AssetCategoryMaxDepth 一致，spec §2） */
export const ASSET_CATEGORY_MAX_DEPTH = 5
/** 「移动到…」里「顶级」这一项的 value：el-select 用 null 当选项值会和「未选择」混掉，改用 -1 */
export const TOP_LEVEL_TARGET = -1

type Cat = AssetCategoryView

const bySort = (a: Cat, b: Cat) => a.sort - b.sort || a.id - b.id

/** 老后端不返回 parent_id（字段整体缺失）→ 全部当顶级 */
function parentOf(cats: Cat[], c: Cat): number | null {
  if (c.parent_id === undefined || c.parent_id === null) return null
  return cats.some(x => x.id === c.parent_id) ? c.parent_id : null // 断链按顶级
}

export function categoryChildren(cats: Cat[], parentId: number | null): Cat[] {
  return cats.filter(c => parentOf(cats, c) === parentId).sort(bySort)
}

export function categoryParentId(cats: Cat[], id: number): number | null {
  const c = cats.find(x => x.id === id)
  return c ? parentOf(cats, c) : null
}

/** 从顶级到该分组的链；不存在返回 [] */
export function categoryPath(cats: Cat[], id: number): Cat[] {
  const path: Cat[] = []
  const seen = new Set<number>()
  let cur = cats.find(x => x.id === id)
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id)
    path.unshift(cur)
    const p = parentOf(cats, cur)
    cur = p === null ? undefined : cats.find(x => x.id === p)
  }
  return path
}

/** 顶级为 1；不存在为 0 */
export function categoryDepth(cats: Cat[], id: number): number {
  return categoryPath(cats, id).length
}

export function categoryPathLabel(cats: Cat[], id: number): string {
  return categoryPath(cats, id).map(c => c.name).join(' / ')
}

/** 含自身，先序 */
export function categorySubtreeIds(cats: Cat[], id: number): number[] {
  if (!cats.some(x => x.id === id)) return []
  const out: number[] = []
  const stack = [id]
  const seen = new Set<number>()
  while (stack.length) {
    const cur = stack.pop() as number
    if (seen.has(cur)) continue
    seen.add(cur)
    out.push(cur)
    for (const child of categoryChildren(cats, cur)) stack.push(child.id)
  }
  return out
}

function categoryHeightRec(cats: Cat[], id: number, seen: Set<number>): number {
  if (!cats.some(x => x.id === id)) return 0
  if (seen.has(id)) return 0 // 成环：到这里封顶，不再往下递归
  seen.add(id)
  const children = categoryChildren(cats, id)
  return 1 + children.reduce((m, c) => Math.max(m, categoryHeightRec(cats, c.id, seen)), 0)
}

/** 以 id 为根的子树高度，只有自己时 1；不存在为 0 */
export function categoryHeight(cats: Cat[], id: number): number {
  return categoryHeightRec(cats, id, new Set<number>())
}

export function categoryIsLeaf(cats: Cat[], id: number): boolean {
  if (!cats.some(x => x.id === id)) return false
  return categoryChildren(cats, id).length === 0
}

export interface FlatCategory {
  category: Cat
  depth: number
  hasChildren: boolean
}

/** 先序展开成树序列表；collapsed 里为 true 的分组不展开其子树。树组件与下拉共用这个顺序（§6.2） */
export function flattenCategoryTree(cats: Cat[], collapsed: Record<number, boolean>): FlatCategory[] {
  const out: FlatCategory[] = []
  const walk = (parentId: number | null, depth: number) => {
    for (const c of categoryChildren(cats, parentId)) {
      const children = categoryChildren(cats, c.id)
      out.push({ category: c, depth, hasChildren: children.length > 0 })
      if (children.length && !collapsed[c.id]) walk(c.id, depth + 1)
    }
  }
  walk(null, 1)
  return out
}

/** 叶子分组，树序：素材「移动到分类」只列这些（素材只能挂叶子，§6.4） */
export function categoryLeaves(cats: Cat[]): Cat[] {
  return flattenCategoryTree(cats, {}).filter(f => !f.hasChildren).map(f => f.category)
}

/** 老后端的列表没有 parent_id 字段：不出现「新增子分组」「移动到…」入口（§7）。
 * 看整份列表而不是只看第一条：否则第一条恰好缺字段、后面条目却带着字段时，树已经按各条自己的
 * parent_id 渲染出两级嵌套，入口却因为只看了第一条被藏起来，自相矛盾 */
export function supportsCategoryHierarchy(cats: Cat[]): boolean {
  return cats.length > 0 && cats.some(c => c.parent_id !== undefined)
}

export function canAddChild(cats: Cat[], id: number): boolean {
  return supportsCategoryHierarchy(cats) && categoryDepth(cats, id) < ASSET_CATEGORY_MAX_DEPTH
}

export interface ReparentTarget {
  value: number // TOP_LEVEL_TARGET = 顶级
  label: string
  depth: number
}

/**
 * 换父可选的目标（§6.2、派生点 b）：排除自己及子孙、排除当前父（原地不动没意义）、排除会超过 5 级的、
 * 排除直接放着素材的叶子（叶子的 count 就是直接数）。已在顶级时不再列「顶级」。
 */
export function reparentTargets(cats: Cat[], id: number): ReparentTarget[] {
  const subtree = new Set(categorySubtreeIds(cats, id))
  const height = categoryHeight(cats, id)
  const currentParent = categoryParentId(cats, id)
  const out: ReparentTarget[] = []
  if (currentParent !== null) out.push({ value: TOP_LEVEL_TARGET, label: '顶级', depth: 0 })
  for (const f of flattenCategoryTree(cats, {})) {
    const c = f.category
    if (subtree.has(c.id)) continue
    if (c.id === currentParent) continue
    if (f.depth + height > ASSET_CATEGORY_MAX_DEPTH) continue
    if (!f.hasChildren && c.count > 0) continue
    out.push({ value: c.id, label: categoryPathLabel(cats, c.id), depth: f.depth })
  }
  return out
}

export interface UploadTarget {
  /** 上传落到哪个分组；null = 未分类 */
  categoryId: number | null
  /** 当前选中的是有子分组的分组，不能往里传，前端给提示、不打开文件选择框 */
  blocked: boolean
}

/**
 * 由分类树的选中值算出上传目标（作者定案 2026-09-18：上传直接落到当前选中的分组）。
 *
 * `selected` 取自 `AssetLibrary.categoryId` 那套约定：`undefined` = 全部、`0` = 未分类、
 * 其余为分组 id。全部与未分类都没有具体归属，进未分类，与改前一致。
 *
 * 选中的分组不在列表里时按 blocked 处理（`categoryIsLeaf` 对不存在的 id 返回 false）：
 * 这是安全方向——宁可让作者重选一次，也不要把素材静默传进一个已经不存在的分组。
 */
export function uploadTarget(cats: Cat[], selected: number | undefined): UploadTarget {
  if (selected === undefined || selected === 0) return { categoryId: null, blocked: false }
  if (!categoryIsLeaf(cats, selected)) return { categoryId: null, blocked: true }
  return { categoryId: selected, blocked: false }
}
